import { useTranslation } from "react-i18next"
import { Button } from "@repo/ui"
import { PropsWithChildren, useState } from "react"
import { Eye } from "@phosphor-icons/react"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
  DialogTrigger,
} from "@/components/ui/dialog"
import {
  RowHeader,
  Table,
  TableBody,
  TableCell,
  TableCellContent,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs"

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"

type PreviewTab = `absolute_value` | `percentage_of_revenue`

export const OpexModellingPreviewDialogTrigger = () => {
  const { t: commonT } = useTranslation(`common`)
  return (
    <DialogTrigger asChild>
      <Button size="sm" variant="secondary" className="w-fit">
        <Eye className="w-4 h-4 mr-1" />
        {commonT(`buttons.preview`)}
      </Button>
    </DialogTrigger>
  )
}
export const OpexModellingPreviewDialog = ({ children }: PropsWithChildren) => {
  const { t } = useTranslation(`opex`)
  const { t: commonT } = useTranslation(`common`)
  const [tab, setTab] = useState<"absolute_value" | "percentage_of_revenue">()
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  return (
    <Dialog
      open={isDialogOpen}
      onOpenChange={(nextOpen) => {
        setTab(nextOpen ? `absolute_value` : `percentage_of_revenue`)
        setIsDialogOpen(nextOpen)
      }}
      modal
    >
      {children}
      <DialogContent className="dialog-default-content">
        <DialogHeader className="h-fit">
          <DialogTitle> {t(`opex.setup.preview.title`)}</DialogTitle>
          <DialogDescription>
            {t(`opex.setup.preview.description`)}
          </DialogDescription>
        </DialogHeader>
        <TooltipProvider>
          <Tabs
            value={tab}
            onValueChange={(value) => setTab(value as PreviewTab)}
          >
            <TabsList className="bg-transparent border-none rounded-none">
              <TabsTrigger value="absolute_value" variant="underline">
                {t(`opex.setup.preview.tabs.absolute_value`)}
              </TabsTrigger>
              <TabsTrigger value="percentage_of_revenue" variant="underline">
                {t(`opex.setup.preview.tabs.percentage_of_revenue`)}
              </TabsTrigger>
            </TabsList>
            <TabsContent value="absolute_value">
              <StaticPreviewTable tab="absolute_value" />
            </TabsContent>
            <TabsContent value="percentage_of_revenue">
              <StaticPreviewTable tab="percentage_of_revenue" />
            </TabsContent>
          </Tabs>
        </TooltipProvider>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="secondary">{commonT(`buttons.close`)}</Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

const StaticPreviewTable = (props: { tab: PreviewTab }) => {
  const { t: opexT } = useTranslation(`opex`)
  return (
    <div className="overflow-auto flex flex-col gap-6 w-full">
      <Table>
        <TableHeader>
          <TableRow className="bg-transparent hover:bg-transparent">
            <TableHead className="w-1/3" />
            <TableHead className="w-2/3 text-center">
              {opexT(`opex.setup.preview.table.header.fy`)}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {props.tab === `percentage_of_revenue` ? (
            <TableRow className="bg-transparent hover:bg-transparent">
              <TableCell className="w-1/3">
                <RowHeader className="justify-start w-full text-secondary">
                  {opexT(`opex.setup.preview.table.rows.total_revenue`)}
                </RowHeader>
              </TableCell>
              <Tooltip>
                <TooltipTrigger asChild>
                  <TableCell className="w-2/3 bg-gray-50">
                    <TableCellContent className="justify-end w-full text-black max-w-none">
                      X,000 €
                    </TableCellContent>
                  </TableCell>
                </TooltipTrigger>
                <TooltipContent>
                  {opexT(`opex.setup.preview.table.tooltips.total_revenue`)}
                </TooltipContent>
              </Tooltip>
            </TableRow>
          ) : null}

          {props.tab === `percentage_of_revenue` ? (
            <TableRow className="bg-transparent hover:bg-transparent">
              <TableCell className="w-1/3">
                <RowHeader className="justify-start w-full text-secondary">
                  {opexT(`opex.setup.preview.table.rows.percentage_of_revenue`)}
                </RowHeader>
              </TableCell>
              <TableCell className="w-2/3">
                <TableCellContent className="justify-end w-full max-w-none">
                  X,000 € (Input field)
                </TableCellContent>
              </TableCell>
            </TableRow>
          ) : null}

          <TableRow
            className={cn(`font-medium`, {
              "bg-blue-50 hover:bg-blue-50":
                props.tab === `percentage_of_revenue`,
              "bg-transparent hover:bg-transparent":
                props.tab === `absolute_value`,
            })}
          >
            <TableCell className="w-1/3">
              <RowHeader className="justify-start w-full font-medium">
                {opexT(`opex.setup.preview.table.rows.amount`)}
              </RowHeader>
            </TableCell>
            <Tooltip>
              <TooltipTrigger asChild>
                <TableCell className="w-2/3">
                  <TableCellContent className="justify-end w-full max-w-none">
                    X,000 €
                  </TableCellContent>
                </TableCell>
              </TooltipTrigger>
              <TooltipContent>
                {opexT(`opex.setup.preview.table.tooltips.amount`)}
              </TooltipContent>
            </Tooltip>
          </TableRow>
        </TableBody>
      </Table>
    </div>
  )
}
